import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiConfig } from '../config/api';

const AdminAuthContext = createContext();

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [superAdminExists, setSuperAdminExists] = useState(null);
  const [checkingSuperAdmin, setCheckingSuperAdmin] = useState(true);

  useEffect(() => {
    // First check if super admin exists
    const checkSuperAdmin = async () => {
      try {
        const API_BASE_URL = apiConfig.baseURL;
        const res = await fetch(`${API_BASE_URL}/api/auth/check-super-admin`);
        const data = await res.json();
        
        if (data.success) {
          setSuperAdminExists(data.data.superAdminExists);
        } else {
          setSuperAdminExists(false);
        }
      } catch (error) {
        console.error('Error checking super admin:', error);
        setSuperAdminExists(false);
      } finally {
        setCheckingSuperAdmin(false);
      }
    };

    checkSuperAdmin();
  }, []);

  useEffect(() => {
    // Don't check session if we're still checking super admin or if super admin doesn't exist
    if (checkingSuperAdmin || superAdminExists === false) {
      if (!checkingSuperAdmin && superAdminExists === false) {
        setLoading(false);
      }
      return;
    }

    const checkAdminSession = () => {
      try {
        const token = localStorage.getItem('adminToken');
        const adminData = localStorage.getItem('adminUser');
        
        if (token && adminData) {
          // Handle case where token might be stored as JSON string
          let tokenString = token;
          if (typeof token !== 'string') {
            try {
              tokenString = JSON.parse(token);
            } catch {
              tokenString = String(token);
            }
          }
          
          // Clean the token (remove whitespace and quotes)
          let cleanToken = tokenString.trim().replace(/^["']|["']$/g, '');
          
          // Try parsing again if it still looks like JSON
          if (cleanToken.startsWith('{') || cleanToken.startsWith('[')) {
            try {
              const parsed = JSON.parse(cleanToken);
              if (typeof parsed === 'string') {
                cleanToken = parsed;
              } else {
                console.log('Token appears to be an object, clearing auth data');
                localStorage.removeItem('adminToken');
                localStorage.removeItem('adminUser');
                setAdmin(null);
                return;
              }
            } catch {
              // Not JSON, continue with cleaned value
            }
          }
          
          // Check if token has valid JWT format (3 parts separated by dots)
          const parts = cleanToken.split('.');
          if (parts.length !== 3) {
            console.log('Invalid admin token format: token does not have 3 parts, clearing auth data');
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminUser');
            setAdmin(null);
            return;
          }
          
          // Update localStorage with cleaned token if it was different
          if (cleanToken !== token) {
            localStorage.setItem('adminToken', cleanToken);
          }
          
          // Check if token is expired
          try {
            const payload = JSON.parse(atob(parts[1]));
            const now = Math.floor(Date.now() / 1000);
            
            if (payload.exp && payload.exp < now) {
              console.log('Admin token expired, clearing auth data');
              localStorage.removeItem('adminToken');
              localStorage.removeItem('adminUser');
              setAdmin(null);
            } else {
              const parsedAdmin = JSON.parse(adminData);
              setAdmin(parsedAdmin);
            }
          } catch (e) {
            console.log('Invalid admin token format, clearing auth data:', e.message);
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminUser');
            setAdmin(null);
          }
        } else {
          setAdmin(null);
        }
      } catch (error) {
        console.error('Error checking admin session:', error);
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        setAdmin(null);
      } finally {
        setLoading(false);
      }
    };

    checkAdminSession();
  }, [checkingSuperAdmin, superAdminExists]);

  const login = async (email, password) => {
    try {
      const API_BASE_URL = apiConfig.baseURL;
      console.log('Admin login attempt to:', `${API_BASE_URL}/api/auth/login`);
      
      // Check cookie consent status
      const cookieConsent = localStorage.getItem('cookieConsent');
      const acceptsCookies = cookieConsent === 'accepted';
      
      const res = await fetch(`${API_BASE_URL}/api/auth/login?admin=true`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Admin-Login': 'true',
          'X-Cookie-Consent': acceptsCookies ? 'accepted' : 'declined'
        },
        credentials: 'include', // Important for cookies
        body: JSON.stringify({ email, password })
      });
      
      console.log('Admin login response status:', res.status);
      
      if (!res.ok) {
        let errorMessage = 'Invalid email or password. Please try again.';
        let redirectToLogin = false;
        try {
          const errorText = await res.text();
          console.error('Admin login failed:', res.status, errorText);
          // Try to parse as JSON to get the message
          try {
            const errorData = JSON.parse(errorText);
            if (errorData.message) {
              errorMessage = errorData.message;
            }
            if (errorData.redirectToLogin) {
              redirectToLogin = true;
            }
          } catch (parseError) {
            // If not JSON, use a generic message
            console.error('Error parsing error response:', parseError);
          }
        } catch (textError) {
          console.error('Error reading error response:', textError);
        }
        return { success: false, error: errorMessage, redirectToLogin };
      }
      
      const data = await res.json();
      console.log('Login response data:', data);
      
      if (!data.success) {
        // Check if OTP is required
        if (data.requiresOTP) {
          return { 
            success: false, 
            error: data.message || 'Please check your email for a verification code.', 
            requiresOTP: true, 
            email: data.email || email 
          };
        }
        return { success: false, error: data.message || 'Invalid email or password. Please try again.' };
      }

      const { user, token } = data.data;
      
      // Validate token exists and is a string
      if (!token) {
        console.error('No token received from server');
        return { success: false, error: 'No token received from server' };
      }
      
      // Ensure token is a string (not an object)
      let tokenString = token;
      if (typeof token !== 'string') {
        console.warn('Token is not a string, converting:', typeof token);
        tokenString = String(token);
      }
      
      // Clean the token (remove whitespace and quotes)
      const cleanToken = tokenString.trim().replace(/^["']|["']$/g, '');
      
      // Validate token format (JWT should have 3 parts separated by dots)
      const parts = cleanToken.split('.');
      
      if (parts.length !== 3) {
        console.error('Invalid token format - expected 3 parts, got:', parts.length);
        console.error('Token value:', cleanToken);
        return { success: false, error: `Invalid token format: expected 3 parts, got ${parts.length}` };
      }
      
      // Final validation before storing
      const finalParts = cleanToken.split('.');
      if (finalParts.length !== 3) {
        console.error('Token validation failed before storing - parts:', finalParts.length);
        return { success: false, error: 'Invalid token format received from server' };
      }
      
      // Validate token length (JWT should be at least 50 characters)
      if (cleanToken.length < 50) {
        console.error('Token is too short before storing! Length:', cleanToken.length);
        console.error('Token value:', cleanToken);
        return { success: false, error: 'Token received from server is too short' };
      }
      
      // Store the cleaned token
      localStorage.setItem('adminToken', cleanToken);
      localStorage.setItem('adminUser', JSON.stringify(user));
      setAdmin(user);
      
      // Verify it was stored correctly
      const storedToken = localStorage.getItem('adminToken');
      const storedParts = storedToken ? storedToken.split('.') : [];
      
      if (!storedToken || storedToken.length < 50) {
        console.error('ERROR: Token was corrupted or truncated when stored!');
        console.error('Original length:', cleanToken.length);
        console.error('Stored length:', storedToken ? storedToken.length : 0);
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        return { success: false, error: 'Token was corrupted during storage' };
      }
      
      if (storedParts.length !== 3) {
        console.error('ERROR: Token format was corrupted when stored!');
        console.error('Original parts:', finalParts.length);
        console.error('Stored parts:', storedParts.length);
        console.error('Stored token value:', storedToken);
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        return { success: false, error: 'Token format was corrupted during storage' };
      }
      
      return { success: true };
    } catch (error) {
      console.error('Admin login network error:', error);
      return { success: false, error: 'Unable to connect to the server. Please check your internet connection and try again.' };
    }
  };

  const logout = () => {
    // Clear all possible token storage locations
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    sessionStorage.removeItem('adminToken');
    sessionStorage.removeItem('adminUser');
    // Also clear generic token keys that might be used
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    setAdmin(null);
  };

  const isAuthenticated = () => !!admin;
  const hasRole = (role) => admin && (admin.role === role || admin.role === 'SUPER_ADMIN');
  
  const verifyOTP = async (email, otpCode) => {
    try {
      // Check cookie consent status
      const cookieConsent = localStorage.getItem('cookieConsent');
      const acceptsCookies = cookieConsent === 'accepted';
      
      const response = await fetch(`${apiConfig.baseURL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Cookie-Consent': acceptsCookies ? 'accepted' : 'declined'
        },
        credentials: 'include', // Important for cookies
        body: JSON.stringify({ email, otpCode })
      });

      const data = await response.json();

      if (data.success) {
        const { user, token } = data.data;
        const cleanToken = token.trim().replace(/^["']|["']$/g, '');
        
        localStorage.setItem('adminToken', cleanToken);
        localStorage.setItem('adminUser', JSON.stringify(user));
        setAdmin(user);
        
        return { success: true, user };
      } else {
        return { success: false, error: data.message || 'Invalid OTP code. Please try again.' };
      }
    } catch (error) {
      console.error('OTP verification network error:', error);
      return { success: false, error: 'Unable to connect to the server. Please check your internet connection and try again.' };
    }
  };

  const resendOTP = async (email) => {
    try {
      const response = await fetch(`${apiConfig.baseURL}/api/auth/resend-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();
      return { success: data.success, message: data.message };
    } catch (error) {
      console.error('Resend OTP error:', error);
      return { success: false, message: 'Failed to resend OTP. Please try again.' };
    }
  };

  const authHeader = () => {
    const token = localStorage.getItem('adminToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const value = { 
    admin, 
    setAdmin,
    loading: loading || checkingSuperAdmin, 
    login, 
    logout, 
    isAuthenticated, 
    hasRole, 
    authHeader,
    superAdminExists,
    checkingSuperAdmin,
    verifyOTP,
    resendOTP
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};
